<template>
  <div>
    <v-card>
      <v-card-title class="py-3">
        <data-avatar
          :icon="assignment.icon"
          :color="assignment.color"
          size="36"
        ></data-avatar>
        <span class="ml-3 text-subtitle-1 font-weight-medium">{{
          assignment.title
        }}</span>
        <v-spacer></v-spacer>
        <v-btn
          v-if="!usedInDetailView && hasEditPermission"
          depressed
          small
          :to="{
            name: 'member-management-assignments-assignment-edit',
            params: {
              organizationId: $route.params.organizationId,
              itemId: assignment.meta.id,
            },
          }"
        >
          <v-icon small left>mdi-pencil</v-icon>Bearbeiten</v-btn
        >
        <template
          v-if="
            (usedInDetailView && hasReadPermission) ||
            assignment.analysis.isShown
          "
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip label small v-bind="attrs" v-on="on" class="mr-1">
                <v-icon x-small left>mdi-dumbbell</v-icon>
                <span class="text-caption">{{
                  getPriorityText(assignment.priorityScore)
                }}</span>
              </v-chip>
            </template>
            <span>Priorität</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip label small v-bind="attrs" v-on="on" class="mr-1">
                <v-icon x-small left>mdi-briefcase-upload-outline</v-icon>
                <span class="text-caption">{{ assignment.workloadScore }}</span>
              </v-chip>
            </template>
            <span>Aufwandswert</span>
          </v-tooltip>
          <assignment-status-chip
            :count="assignment.assignees.length"
            :minThreshold="assignment.analysis.minAssignees"
            :maxThreshold="assignment.analysis.maxAssignees"
            small
          ></assignment-status-chip>
        </template>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-3">
        <!-- Description -->
        <div v-if="assignment.description" class="mb-3">
          <div
            class="text-caption text-uppercase font-weight-medium grey--text mb-1"
          >
            Tätigkeitsbeschreibung
          </div>
          <v-card outlined>
            <v-card-text>
              {{ assignment.description }}
            </v-card-text>
          </v-card>
        </div>

        <!-- Assignees -->
        <div class="mb-3">
          <div
            class="text-caption text-uppercase font-weight-medium grey--text mb-2"
          >
            Zugewiesen ({{ assignment.assignees.length }})
          </div>
          <div
            v-if="assignment.assignees.length === 0"
            class="text-body-2 grey--text"
          >
            Keine Mitglieder zugewiesen.
          </div>
          <v-row v-else dense class="match-height">
            <v-col
              v-for="assignee in sortedAssignees"
              :key="assignee.user.uid"
              cols="12"
              sm="4"
              md="3"
            >
              <v-card outlined>
                <v-list-item dense>
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2 font-weight-medium">
                      {{ assignee.user.displayName }}
                    </v-list-item-title>
                    <v-list-item-subtitle
                      v-if="assignee.position"
                      class="text-caption font-weight-regular"
                    >
                      {{ assignee.position }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action v-if="assignee.isLeader" class="my-0">
                    <v-chip color="warning" label x-small>Leitung</v-chip>
                  </v-list-item-action>
                </v-list-item>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Contact Info -->
        <div v-if="hasContactInfo">
          <div
            class="text-caption text-uppercase font-weight-medium grey--text mb-2"
          >
            Kontakt & Links
          </div>
          <v-row dense>
            <v-col
              v-for="(email, index) in assignment.contact.emails"
              :key="'email-' + index"
              cols="12"
              sm="6"
              md="3"
            >
              <v-card outlined>
                <v-list-item dense>
                  <v-list-item-icon class="mr-2 my-2">
                    <v-icon small>mdi-email</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content class="py-1">
                    <v-list-item-title class="text-body-2">
                      <a
                        :href="`mailto:${email.adress}`"
                        class="text-decoration-none"
                      >
                        {{ email.adress }}
                      </a>
                    </v-list-item-title>
                    <v-list-item-subtitle
                      class="text-caption font-weight-regular"
                      >{{ email.title }}</v-list-item-subtitle
                    >
                  </v-list-item-content>
                </v-list-item>
              </v-card>
            </v-col>

            <v-col
              v-for="(link, index) in assignment.contact.links"
              :key="'link-' + index"
              cols="12"
              sm="6"
              md="3"
            >
              <v-card outlined>
                <v-list-item dense>
                  <v-list-item-icon class="mr-2 my-2">
                    <v-icon small>mdi-link</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content class="py-1">
                    <v-list-item-title class="text-body-2">
                      <a
                        :href="link.url"
                        target="_blank"
                        class="text-decoration-none"
                      >
                        {{ getHostname(link.url) }}
                      </a>
                    </v-list-item-title>
                    <v-list-item-subtitle
                      class="text-caption font-weight-regular"
                      >{{ link.title }}</v-list-item-subtitle
                    >
                  </v-list-item-content>
                </v-list-item>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import DataAvatar from "@/components/_systemwide/DataAvatar.vue";
import AssignmentStatusChip from "@/components/member-management/assignments/AssignmentStatusChip.vue";
import {
  MEMBER_MANAGEMENT_ASSIGNMENTS_read,
  MEMBER_MANAGEMENT_ASSIGNMENTS_update,
} from "@/data/permission-types.js";
export default {
  name: "assignment-detail-card",
  components: { DataAvatar, AssignmentStatusChip },
  props: {
    assignment: {
      type: Object,
      required: true,
    },
    usedInDetailView: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {};
  },
  computed: {
    hasContactInfo() {
      return (
        this.assignment.contact.emails.length > 0 ||
        this.assignment.contact.links.length > 0
      );
    },
    sortedAssignees() {
      return [...this.assignment.assignees].sort(
        (a, b) => b.isLeader - a.isLeader,
      );
    },
    hasReadPermission() {
      return this.$store.getters["organization/checkPermissionByID"](
        `${MEMBER_MANAGEMENT_ASSIGNMENTS_read}`,
      );
    },
    hasEditPermission() {
      return this.$store.getters["organization/checkPermissionByID"](
        `${MEMBER_MANAGEMENT_ASSIGNMENTS_update}`,
      );
    },
  },
  methods: {
    getHostname(url) {
      return new URL(url).hostname;
    },
    getPriorityText(priority) {
      switch (priority) {
        case 1:
          return "Sehr hoch";
        case 2:
          return "Hoch";
        case 3:
          return "Mittel";
        case 4:
          return "Gering";
        case 5:
          return "Sehr gering";
        default:
          return "";
      }
    },
  },
};
</script>
